// Client entry. Wires the daily → sim → loop → renderer, and records the input log.

import './style.css'
import { buildDaily, buildDailyForKey, buildTemplateSample } from './engine/daily'
import { Simulation, TICK_HZ } from './engine/sim'
import { scoreRun } from './engine/score'
import { recordRun } from './engine/stats'
import { PaddockRenderer } from './render/canvas'
import { InputRecorder } from './ui/input'
import { CommandBar } from './ui/commandbar'
import { ResultsScreen, resultsView, readoutsHtml } from './ui/results'
import { Screens } from './ui/screens'
import { renderCommandHelp } from './ui/about'
import { mountAccount } from './ui/account'
import { actionForKey, shouldPreventDefault } from './ui/keyboard'
import { againSearch, isRanked, modeLabel, parseMode } from './ui/mode'
import { renderPicker } from './ui/picker'
import { Announcer, stateMessage } from './ui/announce'
import { AudioKit, soundsFor, type SoundSnapshot } from './ui/audio'
import { browserStorage, loadStats, saveStats } from './ui/storage'
import { displayStreak, type Stats } from './engine/stats'
import { startLoop } from './loop'
import type { InputEvent, RunLog } from './engine/types'

const el = (id: string): HTMLElement => {
  const node = document.getElementById(id)
  if (!node) throw new Error(`missing #${id}`)
  return node
}

const canvas = el('paddock') as HTMLCanvasElement
const hudDay = el('hud-day')
const hudPenned = el('hud-penned')
const hudTime = el('hud-time')
const hudBarks = el('hud-barks')

const results = new ResultsScreen(el('results'))
const screens = new Screens({
  landing: el('landing'),
  about: el('about'),
  paused: el('paused'),
  results: el('results'),
})
const store = browserStorage()
const announcer = new Announcer(el('announce'))
const audio = new AudioKit(store)

/**
 * Sound is driven from the render callback off two snapshots of the run, never from the sim.
 * Nothing here feeds back, so whether audio is on cannot affect determinism or a replay.
 */
let lastSound: SoundSnapshot = {
  barks: 0,
  penned: 0,
  order: null,
  lifted: false,
  panicking: 0,
  won: false,
}

// PHASE 4: fetch /api/daily instead, so the client and server agree on the puzzle by construction
// rather than by both computing it. The engine is shared, so today they already agree.
const today = buildDaily()
const mode = parseMode(window.location.search, today.key)

/**
 * The puzzle being played.
 *
 * Archive days are built from their own key, so an old daily is bit-for-bit the puzzle that was set
 * that day. Practice builds a template sample directly — there is no day, so it borrows today's
 * number purely to keep the shape of `daily` and never posts anything anywhere.
 */
const daily =
  mode.kind === 'archive' || mode.kind === 'rerun'
    ? buildDailyForKey(mode.key)
    : mode.kind === 'practice'
      ? (() => {
          const sample = buildTemplateSample(mode.templateId, mode.index)
          return { key: today.key, seed: sample.seed, day: today.day, level: sample.level }
        })()
      : today

const ranked = isRanked(mode)
const sim = new Simulation(daily.level, daily.seed)

const renderer = new PaddockRenderer(canvas, daily.level.world)
// Direct pointer control is off — the dog is worked by command now. The scheme is retained behind
// this flag rather than deleted; see PLAN.md § Playtest feedback P3.
const input = new InputRecorder(canvas, renderer)
input.bindTickSource(() => sim.tick)
input.bindDogSource(() => sim.dog)

const bar = new CommandBar(el('commands'), (action) => {
  if (action === 'bark') input.bark()
  else input.command(action)
})

hudDay.textContent =
  mode.kind === 'practice' ? 'BIG DOG · PRACTICE' : `BIG DOG ${daily.day}`

// --- Screens -------------------------------------------------------------------------------
//
// The run does not begin until the player says so. Until this landed the loop started on module
// load, so the clock was already running while a first-time visitor read the page — against a par
// time, which made the daily quietly unfair to anyone who paused to look at it.

/** Where BACK returns to, so the help screen can be reached from anywhere that offers it. */
let helpReturn: string | null = 'landing'
let started = false
let paused = false

/**
 * Has today's ranked daily already been completed on this device?
 *
 * A run is only recorded when it finishes, and there is no fail state — so this is "today is done",
 * not "today was attempted". Someone who reloads mid-run has not used their go.
 */
function dailyIsDone(stats: Stats): boolean {
  return stats.history.some((h) => h.key === today.key)
}

function showLanding(): void {
  const label = modeLabel(mode)
  const banner = el('landing-mode')
  const stats = loadStats(store)

  /**
   * Once today's daily is done, PLAY becomes an unranked rerun.
   *
   * Not a nicety: the results panel now offers a way back here, so without this the shortest path
   * from a finished run is MENU → PLAY → a second ranked submission for the same day. Locally that
   * is harmless (`recordRun` keeps the better score and touches no counter), but the hub takes a
   * score per submission, and a leaderboard you can farm by replaying until the number is good is
   * not a leaderboard. One go a day is the promise; the paddock stays open for nothing.
   */
  const done = mode.kind === 'daily' && dailyIsDone(stats)
  el('landing-play').textContent = done ? 'PLAY AGAIN' : 'PLAY'
  banner.textContent = done ? "TODAY'S RUN IS DONE · THIS ONE WON'T COUNT" : label
  banner.hidden = !done && label === ''
  el('landing-stats').innerHTML = readoutsHtml([
    { label: 'STREAK', value: String(displayStreak(stats, daily.key)) },
    { label: 'PLAYED', value: String(stats.played) },
    { label: 'BEST', value: String(stats.bestScore) },
  ])
  screens.show('landing')
}

renderCommandHelp(el('about-commands'))
renderPicker(el('landing-picker'), today.key, mode)
// Fire-and-forget: the account slot stays hidden unless the hub is configured AND reachable, so a
// slow or absent hub delays nothing the player is waiting for.
void mountAccount(el('account'), showLanding)

// The sound button, and the gesture that unlocks audio at all. iOS will not start an AudioContext
// outside a user gesture, so the first tap on PLAY is where it has to happen.
const soundButton = el('hud-sound') as HTMLButtonElement
function paintSound(): void {
  soundButton.classList.toggle('is-off', audio.isMuted)
  soundButton.setAttribute('aria-pressed', String(!audio.isMuted))
  soundButton.setAttribute('aria-label', audio.isMuted ? 'Sound off' : 'Sound on')
}
soundButton.addEventListener('click', () => {
  audio.setMuted(!audio.isMuted)
  audio.unlock()
  paintSound()
})
paintSound()

el('landing-play').addEventListener('click', () => {
  // Today already spent? Then PLAY is the unranked rerun, and that is a navigation rather than a
  // start — the same one the results panel offers.
  if (mode.kind === 'daily' && dailyIsDone(loadStats(store))) {
    window.location.assign(window.location.pathname + againSearch(mode))
    return
  }
  audio.unlock()
  started = true
  el('hud-pause').hidden = false
  screens.show(null)
})

el('landing-help').addEventListener('click', () => {
  helpReturn = 'landing'
  screens.show('about')
})

/**
 * Pause stops the clock, and that is a par-time exploit unless it is accounted for — so both events
 * go in the log. The policy (a hard cap, a diminishing allowance, or charging paused wall-clock
 * after some grace) is deliberately still open and belongs with real data; the *logging* cannot
 * wait for it, because every run submitted before it exists would be unverifiable.
 */
el('hud-pause').addEventListener('click', () => {
  if (!started || finished || paused) return
  paused = true
  input.pause()
  // The bar sits outside the overlay in the layout, so disable it rather than relying on the
  // overlay to swallow input — a Tab key would otherwise reach a command button through the modal.
  bar.setEnabled(false)
  screens.show('paused')
})

function resume(): void {
  paused = false
  input.resume()
  bar.setEnabled(true)
  screens.show(null)
}

el('paused-resume').addEventListener('click', resume)

el('paused-help').addEventListener('click', () => {
  helpReturn = 'paused'
  screens.show('about')
})

el('results-help').addEventListener('click', () => {
  helpReturn = 'results'
  screens.show('about')
})

/**
 * Play again, and go home.
 *
 * Both navigate rather than resetting in place, and that is deliberate rather than lazy. The sim,
 * renderer, input recorder, command bar, announcer and audio kit are all built once at module load
 * from `daily`; tearing that down and rebuilding it is a second lifecycle to keep correct, and the
 * mode already lives in the URL precisely so a run is addressable. A reload gives a genuinely fresh
 * run with no state carried over — which, for a game whose whole promise is determinism, is worth
 * more than saving a page load.
 *
 * **"Again" is always unranked** (`againSearch`), including for today's daily. See ui/mode.ts.
 */
el('results-again').addEventListener('click', () => {
  window.location.assign(window.location.pathname + againSearch(mode))
})

el('results-menu').addEventListener('click', () => {
  window.location.assign(window.location.pathname)
})

el('about-back').addEventListener('click', () => screens.show(helpReturn))

/**
 * Keyboard control. The mapping itself is a pure function in ui/keyboard.ts — this only turns the
 * decision into the same calls the buttons make, so a key and a tap are indistinguishable to the
 * engine and therefore to the input log.
 */
window.addEventListener('keydown', (e) => {
  // Never steal a key from a focused control or a modifier combination.
  if (e.metaKey || e.ctrlKey || e.altKey) return
  const action = actionForKey(e.key, screens.current !== null, started)
  if (!action) return
  if (shouldPreventDefault(e.key)) e.preventDefault()

  switch (action.kind) {
    case 'command':
      input.command(action.command)
      break
    case 'bark':
      input.bark()
      break
    case 'pause':
      el('hud-pause').click()
      break
    case 'resume':
      if (screens.current === 'paused') resume()
      break
  }
})

showLanding()

// Test/debug handle. Used by the headless verification harness to replay an input log through the
// real browser build and compare its state hash against node's — which is how the determinism
// guarantee is checked across engines rather than merely asserted.
declare global {
  interface Window {
    __bigdog?: {
      sim: Simulation
      daily: typeof daily
      /** Step the sim directly, bypassing the render loop, for fast headless replay. */
      replay(events: InputEvent[], ticks: number): { won: boolean; hash: string; penned: number }
      /** The live input log, so the gate can check that pauses are actually recorded. */
      events(): readonly InputEvent[]
    }
  }
}
window.__bigdog = {
  sim,
  daily,
  events: () => input.events,
  replay(events, ticks) {
    const fresh = new Simulation(daily.level, daily.seed)
    const byTick = new Map<number, InputEvent[]>()
    for (const ev of events) {
      const b = byTick.get(ev.tick)
      if (b) b.push(ev)
      else byTick.set(ev.tick, [ev])
    }
    for (let t = 0; t < ticks && !fresh.won; t++) fresh.step(byTick.get(t) ?? [])
    return { won: fresh.won, hash: fresh.stateHash(), penned: fresh.pennedCount }
  },
}

let finished = false

const loop = startLoop(
  () => {
    // Gate the LOOP, never the sim: no ticks pass, so no world time passes and elapsed — which is
    // derived from sim.tick — simply does not advance. The accumulator is still drained by this
    // early return, which is what stops a burst of catch-up steps the moment the run starts.
    if (!started || paused || finished) return
    sim.step(input.drain(sim.tick))
    if (sim.won) {
      finished = true
      onFinish()
    }
  },
  () => {
    renderer.render(sim)
    hudPenned.textContent = `${sim.pennedCount}/${sim.sheep.length}`
    hudTime.textContent = `${(sim.tick / TICK_HZ).toFixed(1)}s`
    hudBarks.textContent = String(sim.barks)
    bar.setActive(sim.order, sim.lifted, sim.pressSteps, sim.steady, sim.backSteps)
    bar.setBarkReady(sim.barkReady)
    // Polite, and only on a real change — the Announcer swallows repeats, which is what stops the
    // clock ticking sixty announcements a second into a screen reader.
    announcer.say(stateMessage(sim.order, sim.lifted, sim.pennedCount, sim.sheep.length))

    const snap: SoundSnapshot = {
      barks: sim.barks,
      penned: sim.pennedCount,
      order: sim.order,
      lifted: sim.lifted,
      panicking: sim.sheep.reduce((n, s) => n + (s.panicked ? 1 : 0), 0),
      won: sim.won,
    }
    for (const sound of soundsFor(lastSound, snap)) audio.play(sound)
    lastSound = snap
  },
)

function onFinish(): void {
  loop.stop()
  bar.setEnabled(false)
  el('hud-pause').hidden = true
  const result = sim.result()
  const score = scoreRun(result, daily.level)

  /**
   * Only today's daily touches the record.
   *
   * An archive or practice run that folded into stats would inflate "played", and one that reached
   * the hub would put a score for a puzzle nobody was set onto a shared leaderboard. `isRanked` is
   * the single place that is decided; this is the single place it is applied.
   */
  const stats = ranked
    ? (() => {
        const next = recordRun(loadStats(store), {
          key: daily.key,
          score: score.primary,
          durationMs: score.durationMs,
          won: result.won,
        })
        saveStats(store, next)
        return next
      })()
    : loadStats(store)

  announcer.say(
    result.won
      ? `Flock penned. ${score.primary} out of 100.`
      : `Run over. ${result.penned} of ${result.flockSize} penned.`,
  )

  results.render(
    resultsView({
      day: daily.day,
      dayKey: daily.key,
      level: daily.level,
      result,
      score,
      stats,
    }),
  )
  screens.show('results')

  const log: RunLog = {
    levelId: daily.level.id,
    seed: daily.seed,
    dailyKey: daily.key,
    events: input.events,
    ticks: sim.tick,
  }

  console.log('run complete', { result, score })

  // Unranked runs are never submitted. The server would reject an archive log anyway — it checks
  // the daily key — but not sending it is the honest version, and it keeps practice entirely local.
  if (!ranked) return

  // PHASE 4: POST the log to /api/verify; the server replays it on the same engine and derives the
  // score itself, then relays to the hub. Until then this is local-only.
  void fetch('/api/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(log),
  }).catch(() => {
    // Server not running in `vite dev` without `npm run dev:server` — the game is still playable.
  })
}
